import { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import PaymentModal from "@/components/PaymentModal";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Check, ShieldCheck, BadgeCheck, MessagesSquare, Flame, MapPin, Sparkles, Gift, Smartphone, Loader2, Lock,
} from "lucide-react";

const PERKS = [
  { icon: MessagesSquare, title: "Unlimited buying & selling", text: "List as many items as you want and message any seller on campus." },
  { icon: BadgeCheck, title: "Verified Student Trust Badge", text: "A verified badge on your listings and profile that builds instant trust." },
  { icon: Smartphone, title: "In-app chat with read-aloud", text: "Negotiate in real time, with text-to-speech to read messages on the go." },
  { icon: Flame, title: "Priority on Most Bought", text: "Your active listings get a boost on the popular Most Bought tab." },
  { icon: MapPin, title: "Campus pickup coordinator", text: "Set and share verified handoff points directly inside a chat." },
];

const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

function Perks({ className = "" }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-2xl p-8 ${className}`}>
      <h3 className="font-display font-bold text-xl text-ink">What your pass unlocks</h3>
      <div className="mt-6 space-y-5">
        {PERKS.map((p) => (
          <div key={p.title} className="flex gap-4" data-testid={`perk-${p.title}`}>
            <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center shrink-0"><p.icon size={18} /></div>
            <div>
              <div className="font-semibold text-ink flex items-center gap-2">{p.title} <Check size={14} className="text-emerald-500" /></div>
              <div className="text-sm text-slate-500 mt-0.5 leading-relaxed">{p.text}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-6 border-t border-slate-100 flex items-start gap-2 text-xs text-slate-500">
        <Lock size={14} className="text-emerald-500 mt-0.5 shrink-0" />
        Payments go directly to ReKTR over UPI — no third-party gateway, no card details ever stored.
      </div>
    </div>
  );
}

export default function Membership() {
  const { user, refreshUser, membershipConfig } = useAuth();
  const { openAuth } = useOutletContext();
  const [payOpen, setPayOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [trialUsed, setTrialUsed] = useState(false);

  const isMember = user && (user.is_member || user.role === "founder");
  const preview = membershipConfig?.preview_active;
  const rupees = membershipConfig?.amount_rupees ?? 29;
  const previewEnds = membershipConfig?.preview_ends_at ? fmtDate(membershipConfig.preview_ends_at) : "";

  useEffect(() => {
    if (user) api.get("/subscription").then(({ data }) => setTrialUsed(!!data.trial_used)).catch(() => {});
  }, [user]);

  const startTrial = async () => {
    if (!user) return openAuth();
    setStarting(true);
    try {
      await api.post("/membership/start-trial");
      await refreshUser();
      setTrialUsed(true);
      toast.success("Your 1-month free trial is active. Happy trading!");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, "Could not start your trial"));
    } finally { setStarting(false); }
  };

  const openPay = () => {
    if (!user) return openAuth();
    if (isMember) return toast.info("You already have an active Campus Pass.");
    setPayOpen(true);
  };

  const onSuccess = async () => { await refreshUser(); toast.success("Campus Pass activated. Happy trading!"); };

  const ActiveBox = (
    <div className="mt-7 rounded-xl bg-emerald-500/15 border border-emerald-400/30 p-4 flex items-center gap-3" data-testid="membership-active">
      <BadgeCheck size={22} className="text-emerald-400" />
      <div>
        <div className="font-semibold">Your pass is active</div>
        <div className="text-xs text-slate-300">
          {user?.member_until ? `Renews ${fmtDate(user.member_until)}` : "Founder access"}
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 lg:px-8 py-12 lg:py-16">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600">
          <Sparkles size={14} className="text-emerald-500" /> {preview ? "Introductory preview period" : "One simple plan for everyone"}
        </div>
        <h1 className="mt-5 font-display font-extrabold text-ink text-4xl lg:text-5xl tracking-tight">The ReKTR Campus Pass</h1>
        <p className="mt-4 text-slate-600 text-lg leading-relaxed">
          {preview
            ? "For our first 10 days, buying and selling is completely free for every verified SRM student — no membership needed. Try everything!"
            : "A membership keeps the community verified, spam-free and trustworthy — for less than a cup of campus coffee."}
        </p>
      </div>

      {preview ? (
        <div className="mt-10 max-w-3xl mx-auto space-y-6">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="preview-banner">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-500 text-white grid place-items-center mb-4"><Gift size={26} /></div>
            <h2 className="font-display font-extrabold text-2xl text-ink">🎉 It's free right now</h2>
            <p className="mt-2 text-slate-600">
              Buying &amp; selling is free for everyone{previewEnds && <> until <span className="font-semibold text-ink">{previewEnds}</span></>}.
            </p>
            <p className="mt-1 text-sm text-slate-500">After that, the Campus Pass is just ₹{rupees}/month — and your first month is on us.</p>
            <Link to="/marketplace">
              <Button className="mt-6 h-11 bg-ink hover:bg-slate-800 text-white rounded-full px-6" data-testid="preview-browse-btn">Browse the marketplace</Button>
            </Link>
          </div>
          <Perks />
        </div>
      ) : (
        <div className="mt-12 grid lg:grid-cols-5 gap-6 items-start">
          <div className="lg:col-span-2 bg-ink text-white rounded-2xl p-8 relative overflow-hidden" data-testid="membership-price-card">
            <div className="absolute inset-0 grid-texture opacity-10" />
            <div className="relative">
              <div className="label-mono text-emerald-300/80">Monthly membership</div>
              <div className="mt-3 flex items-end gap-2">
                <span className="font-display font-extrabold text-6xl leading-none">₹{rupees}</span>
                <span className="text-slate-400 mb-1.5">/ month</span>
              </div>
              {!isMember && !trialUsed && (
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 px-3 py-1 text-xs font-medium text-emerald-300" data-testid="first-month-free-badge">
                  <Gift size={13} /> Your first month is free
                </div>
              )}
              <p className="mt-4 text-slate-300 text-sm leading-relaxed">
                {!isMember && !trialUsed
                  ? "Start with a full month free. After that it's ₹29/month, paid easily over UPI. Cancel anytime."
                  : "Billed monthly over UPI. Required to unlock buying, selling and chat."}
              </p>

              {isMember ? ActiveBox : !trialUsed ? (
                <Button onClick={startTrial} disabled={starting} className="mt-7 w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base rounded-xl" data-testid="start-trial-btn">
                  {starting ? <Loader2 className="animate-spin" size={18} /> : "Start 1-month free trial"}
                </Button>
              ) : (
                <Button onClick={openPay} className="mt-7 w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base rounded-xl" data-testid="subscribe-btn">
                  Pay ₹{rupees} to renew
                </Button>
              )}

              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1.5"><Smartphone size={13} /> UPI</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} className="text-emerald-400" /> Paid directly to ReKTR</span>
              </div>
            </div>
          </div>

          <Perks className="lg:col-span-3" />
        </div>
      )}

      <p className="text-center text-sm text-slate-400 mt-10">
        Not sure yet? <Link to="/marketplace" className="text-emerald-600 hover:underline">Browse the marketplace</Link> first.
      </p>

      <PaymentModal open={payOpen} onOpenChange={setPayOpen} onSuccess={onSuccess} />
    </div>
  );
}
